/* LEVEL 1 — 產房：褲子濕濕的（教學關；prolonged ROM, GBS unknown, penicillin allergy） */
import {
  ask,
  C,
  chat,
  clock,
  doc,
  DRAW,
  drawCTG,
  eventBanner,
  findEnt,
  finding,
  type Level,
  me,
  mkEnt,
  note,
  openDialog,
  openZoom,
  playEnding,
  post,
  ptext,
  R,
  renderSide,
  session,
  sessionGuard,
  setZBar,
  spawnAt,
  spend,
  strList,
  T,
  toast,
  walkEnt,
  tr,
  type ZoomCfg,
} from "../../engine";
import { bedHots, drawBedScene } from "../shared/bedside";

const FACTS = {
  leak_self: tr("她自己說是昨天晚上睡前開始濕的", "She says it started getting wet last night before bed"),
  rom_true: tr(
    "其實昨天早上八點多就開始一直滲水（婆婆知道）",
    "Actually it has been leaking steadily since a little after 8 a.m. yesterday (the mother-in-law knows)",
  ),
  leak_char: tr(
    "一直滲，站起來會流出一點，清清的，好像有點偏綠",
    "A constant trickle; a little gushes out when she stands; clear, maybe slightly greenish",
  ),
  odor: tr("沒有臭味", "No bad smell"),
  ctx: tr("肚子偶爾緊，不規則", "Her belly tightens now and then, irregularly"),
  fm: tr("胎動有", "The baby is moving"),
  chill: tr("有點畏寒", "Feels a bit chilly"),
  gbs: tr("好像有做 GBS 吧？不太確定", "I think I had the GBS test? Not really sure"),
  allergy: tr("對盤尼西林過敏（以前吃了起疹子）", "Allergic to penicillin (got a rash from it before)"),
  pmh: tr("沒有慢性病", "No chronic illnesses"),
  popo_cs: tr(
    "婆婆一直覺得破水就要剖腹",
    "The mother-in-law keeps insisting that once the water breaks, it has to be a C-section",
  ),
} as const;
type FactId = keyof typeof FACTS;
const isFact = (x: string): x is FactId => x in FACTS;

const ORD = {
  cbc: tr("抽血：CBC／CRP", "Labs: CBC/CRP"),
  iv: tr("打 IV、LR 輸液", "Start IV, LR fluids"),
  pen: "Penicillin G IV",
  amp: "Ampicillin 2 g IV",
  cef: "Cefazolin 2 g IV",
  clinda: "Clindamycin 900 mg IV",
  apap: tr("Acetaminophen 退燒", "Acetaminophen (fever)"),
  bcx: tr("血液培養", "Blood cultures"),
  peds: tr("通知小兒科待命", "Page peds to stand by"),
  oxy: tr("Oxytocin 引產", "Oxytocin induction"),
} as const;
type OrderId = keyof typeof ORD;
const isOrder = (x: string): x is OrderId => x in ORD;

interface Order {
  at: number;
  ready?: number;
  done?: boolean;
}

/** Finding name for the CBC/CRP result; the lab doc filters on it. */
const LABS = tr("抽血", "Labs");

type ExamId = "temp" | "pad" | "sse" | "ve" | "belly";

interface Vars {
  revealed: Set<FactId>;
  bookRead: boolean;
  ctgSeen: boolean;
  /** CTG finding already logged for the afebrile / febrile trace. */
  ctgLogged: { normal: boolean; fever: boolean };
  padSeen: boolean;
  sse: boolean;
  /** Number of vaginal exams. */
  ve: number;
  bt: { t: number; v: number }[];
  orders: Partial<Record<OrderId, Order>>;
  /** Minute an effective antibiotic started. */
  abx: number | null;
  abxDrug: OrderId | null;
  warned: { pcn?: boolean };
  reaction: number | null;
  reactionAt: number | null;
  fever: number | null;
  autoCall: number | null;
  vsAwake: boolean;
  vsTalked: boolean;
  vsAccepted: boolean;
  vsArriveAt: number | null;
  vsHere: boolean;
  labsSeen: boolean;
  peds: boolean;
  endAt: number | null;
}

const S = session<Vars>();

/* ---------- vitals & knowledge ---------- */

const romHours = (): number => Math.floor(18.5 + S.t / 60);
const temp = (): number => (S.fever ? 38.6 : Number((37.6 + Math.min(0.3, S.t * 0.006)).toFixed(1)));
const fhr = (): number => (S.fever ? 172 : 148);
const drugName = (): string => (S.abxDrug ? ORD[S.abxDrug] : "");

function known(): string {
  const out: string[] = [];
  out.push(
    tr(
      `量過的體溫：${S.bt.map((b) => `${clock(b.t)} ${b.v}°C`).join("；") || "沒有"}`,
      `Temperatures taken: ${S.bt.map((b) => `${clock(b.t)} ${b.v}°C`).join("; ") || "none"}`,
    ),
  );
  out.push(
    tr(
      `檢查與發現：${S.findings.map((f) => `${clock(f.t)} ${f.name}：${f.text}`).join("；") || "無"}`,
      `Exams and findings: ${S.findings.map((f) => `${clock(f.t)} ${f.name}: ${f.text}`).join("; ") || "none"}`,
    ),
  );
  out.push(
    tr(
      `看過產檢手冊：${S.bookRead ? "是（GBS 未做、盤尼西林過敏、O 型 Rh+）" : "否"}`,
      `Read the prenatal booklet: ${S.bookRead ? "yes (GBS not done, penicillin allergy, O Rh+)" : "no"}`,
    ),
  );
  out.push(
    tr(
      `已做處置：${(Object.keys(S.orders) as OrderId[]).map((k) => ORD[k]).join("、") || "無"}${S.reaction ? "；給了盤尼西林類藥物後起疹子，已停" : ""}`,
      `Orders carried out: ${(Object.keys(S.orders) as OrderId[]).map((k) => ORD[k]).join(", ") || "none"}${S.reaction ? "; broke out in a rash after a penicillin-class drug, which was stopped" : ""}`,
    ),
  );
  out.push(
    tr(
      `問出的病史：${[...S.revealed].map((id) => FACTS[id]).join("；") || "無"}`,
      `History obtained: ${[...S.revealed].map((id) => FACTS[id]).join("; ") || "none"}`,
    ),
  );
  out.push(tr(`內診次數：${S.ve}`, `Cervical exams so far: ${S.ve}`));
  return out.join("\n");
}

/* ---------- exam & orders ---------- */

function exam(k: ExamId): void {
  const ch = "bed";
  if (k === "temp") {
    const v = temp();
    S.bt.push({ t: S.t, v });
    me(ch, tr("（量體溫）", "(Takes her temperature)"));
    post(ch, "result", null, tr(`耳溫 ${v}°C`, `Tympanic temp ${v}°C`));
    spend(1);
    return;
  }
  if (k === "pad") {
    S.padSeen = true;
    me(ch, tr("（看護墊）", "(Checks the pad)"));
    finding(
      tr("護墊", "Pad"),
      tr(
        "護墊濕了一大片，淡綠色、稀稀的，沒有血、沒有臭味。",
        "The pad is soaked: pale green and watery, no blood, no odor.",
      ),
    );
    post(
      ch,
      "narr",
      null,
      tr(
        "護墊濕了一大片，淡綠色、稀稀的，沒有血、沒有臭味。",
        "The pad is soaked: pale green and watery, no blood, no odor.",
      ),
    );
    spend(1);
    return;
  }
  if (k === "sse") {
    if (S.sse) {
      note(tr("剛剛做過了。", "You just did that."), ch);
      return;
    }
    S.sse = true;
    me(ch, tr("（無菌窺器檢查）", "(Sterile speculum exam)"));
    const t = tr(
      "美玲姐陪同。子宮頸口有淡綠色液體流出，後穹窿有積液（pooling），石蕊試紙變藍。",
      "Mei-Ling chaperones. Pale green fluid is trickling from the cervical os, there is pooling in the posterior fornix, and the nitrazine paper turns blue.",
    );
    finding(tr("無菌窺器", "Speculum"), t);
    post(ch, "narr", null, t);
    spend(3);
    return;
  }
  if (k === "ve") {
    S.ve++;
    me(ch, tr("（內診）", "(Cervical exam)"));
    if (S.ve === 1) {
      const t = tr(
        "子宮頸開 2 公分、變薄 50%、先露 -2，摸得到羊水在流。",
        "Cervix 2 cm dilated, 50% effaced, station -2; you can feel fluid leaking.",
      );
      finding(tr("內診", "Cervical exam"), t);
      post(ch, "narr", null, t);
    } else
      post(
        ch,
        "npc",
        "meiling",
        S.ve === 2
          ? tr(
              "醫師，破水了不要一直內診，會增加感染。",
              "Doctor, her water's broken. Stop checking her cervix, it raises the infection risk.",
            )
          : tr("……你今天第三次內診了。", "…That's your third cervical exam today."),
      );
    if (S.ve >= 2) S.mood.meiling = (S.mood.meiling ?? 0) + 1;
    spend(2);
    return;
  }
  me(ch, tr("（摸肚子）", "(Palpates her abdomen)"));
  const t = tr(
    "子宮底在劍突下三指，胎頭在下。摸了五分鐘，宮縮一次，持續 30 秒，強度輕。子宮沒有壓痛。",
    "Fundus three fingerbreadths below the xiphoid, head down. Over five minutes: one contraction, 30 seconds, mild. The uterus is nontender.",
  );
  finding(tr("腹部", "Abdomen"), t);
  post(ch, "narr", null, t);
  spend(2);
}

const GIVE_SAY: Record<OrderId, string> = tr(
  {
    cbc: "抽了，大概二十五分鐘。",
    iv: "IV 打好了。",
    pen: "Penicillin 掛上去了。",
    amp: "Ampicillin 掛上去了。",
    cef: "Cefazolin 掛上去了。",
    clinda: "Clindamycin 掛上去了。",
    apap: "退燒藥給了。",
    bcx: "血液培養送了。",
    peds: "小兒科說知道了，生的時候會過來。",
    oxy: "Oxytocin 開始滴了。",
  },
  {
    cbc: "Drawn. About twenty-five minutes.",
    iv: "IV's in.",
    pen: "Penicillin's hanging.",
    amp: "Ampicillin's hanging.",
    cef: "Cefazolin's hanging.",
    clinda: "Clindamycin's hanging.",
    apap: "Gave her the fever reducer.",
    bcx: "Blood cultures sent.",
    peds: "Peds says got it, they'll come down for the delivery.",
    oxy: "Oxytocin's running.",
  },
);

function give(id: OrderId): void {
  if (S.orders[id] && id !== "apap") return;
  const ch = S.dialog === "nurse" ? "nurse" : "bed";
  if ((id === "pen" || id === "amp") && !S.warned.pcn) {
    S.warned.pcn = true;
    me(ch, tr(`（${ORD[id]}）`, `(${ORD[id]})`));
    ask(
      ch,
      "meiling",
      tr(
        "醫師，她手冊上寫盤尼西林過敏耶，你確定？確定的話再說一次。",
        "Doctor, her booklet says she's allergic to penicillin. You sure? If you are, tell me again.",
      ),
    );
    spend(1);
    return;
  }
  if (id === "oxy" && !S.vsAccepted) {
    me(ch, tr(`（${ORD[id]}）`, `(${ORD[id]})`));
    post(
      ch,
      "npc",
      "meiling",
      tr("催生要主任同意喔，你先去報告。", "Induction needs the attending's OK. Go present her first."),
    );
    return;
  }
  me(ch, tr(`（${ORD[id]}）`, `(${ORD[id]})`));
  const o: Order = { at: S.t };
  S.orders[id] = o;
  post(ch, "npc", "meiling", GIVE_SAY[id]);
  if (id === "pen" || id === "amp") {
    S.reactionAt = S.t + 4;
    S.abxDrug = id;
  } else if (id === "cef" || id === "clinda") {
    S.abx = S.t;
    S.abxDrug = id;
  }
  if (id === "cbc") o.ready = S.t + 25;
  if (id === "peds") S.peds = true;
  spend(1);
}

/* ---------- 林主任 ---------- */

function wakeVS(how: "phone" | "knock" | "nurse"): void {
  if (S.vsAwake) return;
  S.vsAwake = true;
  spawnAt("vs", "vs", [16, 2], [16, 3]);
  post(
    "vs",
    "narr",
    null,
    how === "phone"
      ? tr("電話響了很久，林主任才接起來。", "The phone rings a long time before Dr. Lin picks up.")
      : tr(
          "你敲了值班室的門。裡面傳來一聲「……進來。」",
          "You knock on the on-call room door. From inside: “…Come in.”",
        ),
  );
}

function vsWalk(): void {
  const v = findEnt("vs");
  if (!v) return;
  walkEnt(v, [4, 4]);
  v.onArrive = () => {
    v.face = "left";
    S.vsHere = true;
    S.endAt = S.t + 1;
  };
}

/* ---------- endings ---------- */

function endA(): void {
  void playEnding(
    "A",
    tr("順利待產", "Laboring Smoothly"),
    [
      {
        who: "vs",
        text: tr(
          `破水 ${romHours()} 小時、GBS 不明，抗生素上了。開始 oxytocin。`,
          `ROM ${romHours()} hours, GBS unknown, antibiotics are in. Start oxytocin.`,
        ),
        fn: () => {
          S.orders.oxy = { at: S.t };
        },
      },
      { who: "popo", text: tr("不用剖腹喔？", "So no C-section?") },
      { who: "vs", text: tr("目前不用。", "Not for now.") },
      { who: "yizhen", text: tr("那……我先睡一下。", "Then… I'm going to sleep a little.") },
      {
        who: "vs",
        text:
          S.abxDrug === "cef" || S.abxDrug === "clinda"
            ? tr("過敏有注意到，很好。", "You caught the allergy. Good.")
            : tr("下次記得先看過敏。", "Next time, check allergies first."),
      },
    ],
    () =>
      tr(
        `抗生素在 ${S.abx !== null ? clock(S.abx) : "—"} 開始，宜蓁沒有發燒。`,
        `Antibiotics started at ${S.abx !== null ? clock(S.abx) : "—"}, and Yi-Zhen never ran a fever.`,
      ),
  );
}

function endB(): void {
  void playEnding(
    "B",
    tr("產時發燒", "Intrapartum Fever"),
    [
      {
        who: "vs",
        text: tr(
          "發燒、胎心 170。抗生素、退燒、通知小兒科待命。",
          "Febrile, fetal heart 170. Antibiotics, antipyretic, have peds stand by.",
        ),
      },
      { who: "yizhen", text: tr("好冷……", "So cold…") },
      { who: "popo", text: tr("我就說要剖腹！", "I told you she needed a C-section!") },
      {
        who: "vs",
        text: S.orders.peds
          ? tr("小兒科你已經通知了，這點不錯。", "You'd already called peds. That part was good.")
          : tr("破水這麼久，抗生素要早點上。", "With ROM this long, antibiotics have to go in earlier."),
      },
    ],
    () =>
      tr(`宜蓁在 ${clock(S.fever ?? S.t)} 發燒到 38.6°C。`, `Yi-Zhen spiked to 38.6°C at ${clock(S.fever ?? S.t)}.`),
  );
}

/* ---------- close-ups ---------- */

function openCTG(): void {
  S.ctgSeen = true;
  openZoom({
    title: tr("胎兒監視器（CTG）", "Fetal monitor (CTG)"),
    sub: tr("上面是胎心，下面是宮縮", "Top: fetal heart rate. Bottom: contractions."),
    draw: (c, now) => drawCTG(c, now, { fhr: fhr(), lowVar: !!S.fever, ctxEvery: 540, ctxAmp: 18, alarm: !!S.fever }),
    bar: [],
    onBack: () => {
      if (S.dialog === "bed") openZoom({ dlg: "bed", ...bedZoom() });
    },
  });
  const fev = !!S.fever;
  const slot = fev ? "fever" : "normal";
  if (!S.ctgLogged[slot]) {
    S.ctgLogged[slot] = true;
    S.findings.push({
      t: S.t,
      name: "CTG",
      text: fev
        ? tr(
            "胎心 baseline 170 以上，變異性小，沒有加速。宮縮不規則。",
            "FHR baseline above 170, minimal variability, no accelerations. Contractions irregular.",
          )
        : tr(
            "胎心 baseline 145–150，中等變異性，有加速，沒有減速。宮縮不規則、輕微。",
            "FHR baseline 145–150, moderate variability, accelerations present, no decelerations. Contractions irregular and mild.",
          ),
    });
  }
}

function bedZoom(): ZoomCfg {
  return {
    title: tr("待產床", "Labor bed"),
    sub: tr("點監視器、肚子、護墊……", "Tap the monitor, belly, pad…"),
    draw: (c, now) =>
      drawBedScene(c, now, {
        wall: "#ece2ec",
        wall2: "#e0d4e0",
        base: "#c8b4c8",
        floor: "#dccfdc",
        floor2: "#cebfce",
        pt: "yizhen",
        belly: true,
        blanket: "#9fc4a8",
        blanket2: "#7ea88a",
        pale: S.fever ? { s: "#f6d6c8" } : {},
        shiver: !!S.fever,
        iv: !!S.orders.iv,
        ...(S.abx !== null ? { iv2: "#f6e7a6" } : {}),
        pad: { size: 16, color: "#9ab86a" },
        companion: "popo",
        monitor: (mc, mnow) => {
          ptext(mc, "FHR", 11, 9, "#3be37a");
          ptext(mc, fhr(), 11, 16, "#3be37a", 2);
          for (let i = 0; i < 20; i++) {
            const y = 22 + Math.round(2 * Math.sin((i + mnow / 150) * 0.8));
            R(mc, "#4fd6ff", 28 + i, y, 1, 1);
          }
        },
      }),
    hot: () =>
      bedHots({
        monLabel: tr("胎兒監視器", "Fetal monitor"),
        onMonitor: openCTG,
        ptLabel: tr("宜蓁（跟她說話）", "Yi-Zhen (talk to her)"),
        onBelly: () =>
          setZBar(
            [
              C(tr("摸肚子、感覺宮縮", "Palpate, feel for contractions"), () => exam("belly")),
              C(tr("量體溫", "Take temp"), () => exam("temp")),
            ],
            tr("肚子", "Belly"),
          ),
        onPad: () =>
          setZBar(
            [
              C(tr("看護墊", "Check pad"), () => exam("pad")),
              C(tr("無菌窺器檢查", "Sterile speculum exam"), () => exam("sse")),
              C(tr("內診", "Cervical exam"), () => exam("ve")),
            ],
            tr("會陰", "Perineum"),
          ),
        onIv: () =>
          note(
            S.orders.iv
              ? tr("點滴：LR", "IV: LR") + (S.abx !== null ? tr(`、${drugName()}`, `, ${drugName()}`) : "")
              : tr("還沒打 IV。", "No IV yet."),
            "bed",
          ),
        companion: "popo",
        companionLabel: tr("婆婆（跟她說話）", "Mother-in-law (talk to her)"),
      }),
  };
}

/* ---------- dialogs ---------- */

const dialogs: Level["dialogs"] = {
  bed: {
    title: tr("待產床", "Labor bed"),
    sub: tr("宜蓁和婆婆", "Yi-Zhen and her mother-in-law"),
    pics: ["yizhen", "popo"],
    ch: "bed",
    talk: true,
    ph: tr("對宜蓁或婆婆說…", "Say something to Yi-Zhen or her mother-in-law…"),
    choices() {
      return [
        C(tr("床邊特寫", "Bedside close-up"), () => openZoom({ dlg: "bed", ...bedZoom() })),
        C(tr("看胎兒監視器", "Check fetal monitor"), openCTG),
        C(tr("量體溫", "Take temp"), () => exam("temp")),
        "sep",
        C(tr("看護墊", "Check pad"), () => exam("pad")),
        C(tr("無菌窺器檢查", "Sterile speculum exam"), () => exam("sse"), S.sse),
        C(tr("內診", "Cervical exam"), () => exam("ve")),
        C(tr("摸肚子", "Palpate abdomen"), () => exam("belly")),
      ];
    },
    async onTalk(text) {
      await chat(text, {
        ch: "bed",
        think: "yizhen",
        speakers: { yizhen: tr("宜蓁", "Yi-Zhen"), popo: tr("婆婆", "Mother-in-law") },
        body: tr(
          `你在醫學教育遊戲中同時扮演產房待產床旁的兩個人：產婦「宜蓁」和她的婆婆。玩家是婦產科見習醫學生（clerk）。
宜蓁：32 歲上班族，第一胎 38 週，有點緊張，客氣，記性普通，常說「應該吧」。婆婆：65 歲，很有主見、愛插話，覺得破水就該剖腹，對醫生客氣但會唸媳婦。先生在外地出差，正在趕回來。
【隱藏真相，不能說出診斷】破水已超過 18 小時、羊水有胎便、GBS 沒做、對盤尼西林過敏。${S.fever ? "她現在發燒、發抖。" : ""}
【可揭露的事實】被問到才說，括號是 id：
${Object.entries(FACTS)
  .map(([k, v]) => `- (${k}) ${v}`)
  .join("\n")}
特別規則：問「什麼時候開始濕的」，宜蓁會說昨天晚上（leak_self）；婆婆聽到會插話糾正「你昨天早上就說褲子濕了啊」（rom_true）——但只有在被問到時間時才會發生。問過敏時宜蓁會說盤尼西林過敏（allergy）。
【狀態】${clock()}。已揭露：${[...S.revealed].join(",") || "無"}`,
          `In a medical education game you play two people at a labor bed in L&D at the same time: the laboring patient "Yi-Zhen" and her mother-in-law. The player is an OB/GYN clerk (a medical student on clerkship).
Yi-Zhen: 32, office worker, first baby, 38 weeks; a bit nervous, polite, so-so memory, often says "I think so?". The mother-in-law: 65, very opinionated, keeps butting in, believes that once the water breaks it should be a C-section; polite to doctors but nags her daughter-in-law. The husband is away on a business trip and is rushing back.
[HIDDEN TRUTH, never state a diagnosis] Membranes ruptured more than 18 hours ago, the fluid has meconium in it, GBS was never done, penicillin allergy.${S.fever ? " She now has a fever and is shivering." : ""}
[REVEALABLE FACTS] Say each one only when asked; the id is in parentheses:
${Object.entries(FACTS)
  .map(([k, v]) => `- (${k}) ${v}`)
  .join("\n")}
Special rules: asked "when did it start getting wet", Yi-Zhen says last night (leak_self); the mother-in-law, hearing that, butts in to correct her: "You said your pants were wet yesterday morning!" (rom_true). This only happens when they are asked about timing. Asked about allergies, Yi-Zhen says she is allergic to penicillin (allergy).
[STATE] ${clock()}. Revealed so far: ${[...S.revealed].join(",") || "none"}`,
        ),
        extra: `"revealed":[]`,
        after(r) {
          for (const id of strList(r.revealed)) if (isFact(id)) S.revealed.add(id);
        },
      });
    },
  },
  nurse: {
    title: tr("美玲姐", "Mei-Ling"),
    sub: tr("產房資深護理師", "Senior L&D nurse"),
    pics: ["meiling"],
    ch: "nurse",
    talk: true,
    ph: tr("對美玲姐說，也可以口頭下醫囑", "Talk to Mei-Ling, or give a verbal order"),
    choices() {
      const g = (id: OrderId) => () => give(id);
      const o = S.orders;
      return [
        tr("檢查", "Tests"),
        C(tr("抽血：CBC／CRP", "Labs: CBC/CRP"), g("cbc"), !!o.cbc),
        C(tr("血液培養", "Blood cultures"), g("bcx"), !!o.bcx),
        tr("處置", "Treatment"),
        C(tr("打 IV、LR 輸液", "Start IV, LR fluids"), g("iv"), !!o.iv),
        C("Penicillin G IV", g("pen"), !!o.pen),
        C("Ampicillin 2 g IV", g("amp"), !!o.amp),
        C("Cefazolin 2 g IV", g("cef"), !!o.cef),
        C("Clindamycin 900 mg IV", g("clinda"), !!o.clinda),
        C(tr("Acetaminophen 退燒", "Acetaminophen (fever)"), g("apap")),
        C(tr("通知小兒科待命", "Page peds to stand by"), g("peds"), !!o.peds),
        C(tr("Oxytocin 引產", "Oxytocin induction"), g("oxy"), !!o.oxy),
      ];
    },
    async onTalk(text) {
      await chat(text, {
        ch: "nurse",
        think: "meiling",
        speakers: { meiling: tr("美玲姐", "Mei-Ling") },
        cost: 1,
        body: tr(
          `你在醫學教育遊戲中扮演產房資深護理師「美玲姐」，二十年經驗，冷靜、講話快、會帶新人。玩家是婦產科見習醫學生（clerk）。
她知道：宜蓁 38+3 週初產婦，說褲子濕；她已接上 CTG。她看過產檢手冊（GBS 未做、盤尼西林過敏），但不會主動講，除非 clerk 問或要開盤尼西林類的藥。她不會替 clerk 下診斷。
clerk 目前知道與做過的：
${known()}
【可執行的醫囑 id】cbc；bcx 血液培養；iv；pen；amp；cef；clinda；apap；peds 通知小兒科；oxy 引產（主任沒同意前會拒絕）
clerk 明確要求的醫囑，把 id 放進 orders。不要編數字。`,
          `In a medical education game you play "Mei-Ling", a senior L&D nurse with twenty years' experience: calm, talks fast, good at bringing new people along. The player is an OB/GYN clerk (a medical student on clerkship).
What she knows: Yi-Zhen is a primigravida at 38+3 weeks who says her pants are wet; Mei-Ling has already put her on the CTG. She has read the prenatal booklet (GBS not done, penicillin allergy) but won't bring it up on her own unless the clerk asks or tries to order a penicillin-class drug. She does not make the diagnosis for the clerk.
What the clerk currently knows and has done:
${known()}
[ORDER IDS YOU CAN CARRY OUT] cbc; bcx blood cultures; iv; pen; amp; cef; clinda; apap; peds page pediatrics; oxy induction (refuse until the attending has approved it)
For orders the clerk explicitly asks for, put the ids in orders. Do not make up numbers.`,
        ),
        extra: `"orders":[]`,
        after(r) {
          for (const o of strList(r.orders))
            if (isOrder(o)) {
              const cur = S.dialog;
              give(o);
              if (S.dialog !== cur) {
                S.dialog = cur;
                renderSide();
              }
            }
        },
      });
    },
  },
  vsroom: {
    title: tr("值班室", "On-call room"),
    sub: () => (S.vsAwake ? tr("林主任醒了", "Dr. Lin is awake") : tr("林主任在睡覺", "Dr. Lin is asleep")),
    ch: "vsroom",
    choices() {
      return S.vsAwake
        ? [C(tr("跟林主任報告", "Present to Dr. Lin"), () => openDialog("vs"))]
        : [
            C(tr("敲門叫醒主任", "Knock and wake him"), () => {
              wakeVS("knock");
              openDialog("vs");
            }),
          ];
    },
  },
  vs: {
    title: tr("林主任", "Dr. Lin"),
    sub: () =>
      S.vsHere
        ? tr("在待產床旁", "At the labor bed")
        : S.vsAwake
          ? tr("剛被你叫醒", "Just woken up")
          : tr("電話", "Phone"),
    pics: ["vs"],
    ch: "vs",
    talk: true,
    ph: tr("向林主任報告…", "Present to Dr. Lin…"),
    keepOpen: true,
    async onTalk(text) {
      S.vsTalked = true;
      if (!S.vsAwake) wakeVS("phone");
      await chat(text, {
        ch: "vs",
        think: "vs",
        tier: "default",
        speakers: { vs: tr("林主任", "Dr. Lin") },
        body: tr(
          `你在醫學教育遊戲中扮演婦產科主治「林主任」，凌晨三點被叫醒，有點起床氣但會教。話少，用問題逼學生思考，中英夾雜（「ROM 多久？」「GBS 呢？」「有沒有發燒？」）。這是教學關，學生講不完整時，你追問的方式要具體、引導他想到要問的事。不直接說出答案。
【真相】宜蓁 38+3 週初產婦；破水已 ${romHours()} 小時（昨天早上八點多開始，她自己以為昨晚）；羊水淡綠；GBS 未做；盤尼西林過敏。${S.fever ? "她現在已經發燒 38.6、胎心 170。" : ""}正確計畫：收待產、因破水超過 18 小時且 GBS 不明給抗生素（盤尼西林過敏改 Cefazolin 或 Clindamycin）、引產、通知小兒科。
【clerk 實際知道的】（講出以外的資訊就追問「哪裡知道的？」）
${known()}
【action】accept_plan：clerk 講出破水時間超過 18 小時（或昨天早上破水）以及 GBS 不明或胎便，並提出抗生素或引產的計畫。你同意，說你過去看，並提醒一件事。continue：其他情況。`,
          `In a medical education game you play "Dr. Lin", an OB/GYN attending who has just been woken at 3 a.m.: a bit grumpy about it, but he teaches. Few words; he makes the student think by asking questions, in clipped clinical shorthand ("ROM how long?" "GBS?" "Any fever?"). This is the tutorial level: when the student's presentation is incomplete, make your follow-up questions specific, steering them toward what they should have asked. Never give the answer outright.
[TRUTH] Yi-Zhen, primigravida at 38+3 weeks; membranes ruptured ${romHours()} hours ago (it started a little after 8 a.m. yesterday; she herself thinks it was last night); fluid pale green; GBS not done; penicillin allergy.${S.fever ? " She now has a fever of 38.6 and a fetal heart rate of 170." : ""} Correct plan: admit for labor; antibiotics because ROM is over 18 hours and GBS is unknown (penicillin allergy, so cefazolin or clindamycin instead); induction; notify pediatrics.
[WHAT THE CLERK ACTUALLY KNOWS] (if they state anything beyond this, press them: "Where did you get that?")
${known()}
[action] accept_plan: the clerk states that ROM is over 18 hours (or that her water broke yesterday morning) and that GBS is unknown or there is meconium, and proposes antibiotics or induction. You agree, say you're coming over to see her, and remind them of one thing. continue: anything else.`,
        ),
        extra: `"action":"continue"`,
        after(r) {
          if (r.action === "accept_plan" && !S.vsAccepted) {
            if (!S.revealed.has("rom_true")) {
              post(
                "vs",
                "npc",
                "vs",
                tr("等等，你確定是昨晚破的？再去問清楚。", "Hold on. You're sure it broke last night? Go ask again."),
              );
              return;
            }
            S.vsAccepted = true;
            post(
              "vs",
              "narr",
              null,
              tr("林主任抓了白袍，往待產室走。", "Dr. Lin grabs his white coat and heads for the labor room."),
            );
            if (S.abx === null && !S.reactionAt) {
              S.abx = S.t + 2;
              S.abxDrug = "cef";
              S.orders.cef = { at: S.t };
              post(
                "nurse",
                "npc",
                "meiling",
                tr("主任說先上 Cefazolin，我去掛。", "Dr. Lin says start cefazolin. I'll go hang it."),
              );
            }
            vsWalk();
          }
        },
      });
    },
  },
  phone: {
    title: tr("護理站電話", "Nurses’ station phone"),
    sub: tr("要打給誰？", "Who do you call?"),
    ch: "phone",
    choices() {
      return [
        C(tr("打給林主任（值班室）", "Call Dr. Lin (on-call room)"), () => openDialog("vs"), S.vsHere),
        C(tr("打給產檢診所", "Call her prenatal clinic"), () => {
          me("phone", tr("（打給產檢診所）", "(Calls her prenatal clinic)"));
          post(
            "phone",
            "narr",
            null,
            tr(
              "「您好，本診所看診時間為……」半夜只有語音。",
              "“Thank you for calling. Our clinic hours are…” In the middle of the night, there’s only the recording.",
            ),
          );
          spend(1);
        }),
      ];
    },
  },
  book: {
    title: tr("產檢手冊", "Prenatal booklet"),
    sub: tr("宜蓁的媽媽手冊", "Yi-Zhen’s maternal health booklet"),
    ch: "book",
    doc: doc(() => {
      S.bookRead = true;
      return tr<[string, string][]>(
        [
          ["基本資料", "宜蓁，32 歲，G1P0，預產期 2 週後。血型 O 型 Rh(+)。"],
          ["藥物過敏", "Penicillin（皮疹）"],
          ["產檢紀錄", "血壓都正常；妊娠糖尿病篩檢正常；胎位頭位。"],
          ["乙型鏈球菌（GBS）篩檢", "35–37 週：未做（預約 38+5 週）"],
          ["上次產檢", "37+3 週：子宮頸未開。"],
        ],
        [
          ["Basics", "Yi-Zhen, 32, G1P0, due in 2 weeks. Blood type O Rh(+)."],
          ["Drug allergies", "Penicillin (rash)"],
          ["Prenatal record", "BP normal throughout; gestational diabetes screen normal; cephalic presentation."],
          ["Group B strep (GBS) screen", "35–37 weeks: not done (booked for 38+5)"],
          ["Last visit", "37+3 weeks: cervix closed."],
        ],
      );
    }),
  },
  lab: {
    title: tr("電腦：檢驗結果", "Computer: lab results"),
    sub: tr("護理站", "Nurses’ station"),
    ch: "lab",
    doc: doc(() => {
      const cbc = S.orders.cbc;
      return [
        [
          LABS,
          cbc
            ? cbc.done
              ? S.findings
                  .filter((f) => f.name === LABS)
                  .map((f) => f.text)
                  .join("")
              : tr(`等待中，約 ${clock(cbc.ready ?? S.t)} 出來`, `Pending, ready around ${clock(cbc.ready ?? S.t)}`)
            : tr("還沒抽", "Not drawn"),
        ],
        [
          tr("血液培養", "Blood cultures"),
          S.orders.bcx ? tr("已送，要幾天", "Sent; takes a few days") : tr("沒送", "Not sent"),
        ],
      ];
    }),
  },
};

/* ---------- level ---------- */

export const L1: Level = {
  id: "l1",
  no: tr("一", "1"),
  short: tr("第一關　產房", "Level 1 · L&D"),
  title: tr("產房：褲子濕濕的", "L&D: “I Think I’m Leaking”"),
  place: tr("產房待產室", "L&D labor room"),
  blurb: tr(
    "38 週初產婦凌晨說褲子濕濕的。教學關。",
    "A first-time mom at 38 weeks comes in at 3 a.m. saying she’s been leaking. Tutorial.",
  ),
  cast: ["yizhen", "popo", "meiling", "vs", "r2"],
  intro: tr(
    [
      "週二凌晨 03:00，你是今晚跟值班的婦產科 clerk。宜蓁 32 歲，第一胎，38 週，02:40 跟婆婆一起來產房，說褲子濕濕的。",
      "美玲姐已經幫她接上胎兒監視器。林主任在值班室睡覺，小周在產台室接生。美玲姐跟你說：「你先去問，問完跟主任報告。」",
    ],
    [
      "Tuesday, 03:00. You’re the OB/GYN clerk on call tonight. Yi-Zhen, 32, first pregnancy, 38 weeks, came into L&D at 02:40 with her mother-in-law, saying her underwear keeps getting wet.",
      "Mei-Ling has already put her on the fetal monitor. Dr. Lin is asleep in the on-call room, and Chou is in the delivery room delivering a baby. Mei-Ling tells you: “Go take her history, then present to the attending.”",
    ],
  ),
  start: 3 * 60,
  maxT: 70,
  night: true,
  idleSec: 12,
  map: [
    "####################",
    "#aaaaaaa#bbbbb#cccc#",
    "#aaaaaaa#bbbbb#cccc#",
    "#aaaaaaa#bbbbbccccc#",
    "#aaaaaaa#bbbbb#cccc#",
    "#aaaaaaaabbbbb#cccc#",
    "#aaaaaaaabbbbb######",
    "#aaaaaaa#bbbbb#dddd#",
    "#aaaaaaa#bbbbb#dddd#",
    "#aaaaaaa#bbbbbddddd#",
    "#aaaaaaa#bbbbb#dddd#",
    "####################",
  ],
  zones: {
    a: ["#ece2ec", "#dccfdc", "#e4d8e4"],
    b: ["#e9e1cf", "#d8cdb5", "#e0d6c0"],
    c: ["#4a5276", "#3e4566", "#454d70"],
    d: ["#d3e6df", "#bdd6cc", "#c8ded5"],
  },
  windows: [
    [3, 0],
    [4, 0],
    [16, 0],
  ],
  exits: [[11, 11]],
  chName: {
    bed: tr("待產床", "Labor bed"),
    nurse: tr("美玲姐", "Mei-Ling"),
    vs: tr("林主任", "Dr. Lin"),
    phone: tr("電話", "Phone"),
    book: tr("產檢手冊", "Prenatal booklet"),
    lab: tr("檢驗結果", "Lab results"),
    vsroom: tr("值班室", "On-call room"),
    sys: tr("產房", "L&D"),
  },
  objects: [
    { id: "ctg", type: "monitorStand", x: 2, y: 3, label: tr("胎兒監視器（CTG）", "Fetal monitor (CTG)") },
    { id: "bed", type: "l1bed", x: 3, y: 3, h: 2, label: tr("待產床：宜蓁", "Labor bed: Yi-Zhen") },
    { id: "stand", type: "stand", x: 5, y: 2 },
    { id: "book", type: "chart", x: 5, y: 2, label: tr("產檢手冊", "Prenatal booklet") },
    { id: "bed2", type: "bed", x: 3, y: 8, h: 2, label: tr("空床", "Empty bed") },
    { id: "sink", type: "sink", x: 1, y: 10, label: tr("洗手台", "Sink") },
    { id: "counter", type: "counter", x: 9, y: 2, w: 4 },
    { id: "phone", type: "phone", x: 9, y: 2, label: tr("電話", "Phone") },
    { id: "lab", type: "computer", x: 11, y: 2, label: tr("電腦：檢驗結果", "Computer: lab results") },
    { id: "bench", type: "bench", x: 10, y: 9, w: 2 },
    { id: "plant", type: "plant", x: 13, y: 10 },
    { id: "vsbed", type: "l1vsbed", x: 17, y: 2, h: 2, label: tr("值班室的床", "On-call bed"), act: "vsroom" },
    {
      id: "dbed",
      type: "bed",
      x: 16,
      y: 8,
      h: 2,
      pt: true,
      hair: "#3a2a22",
      skin: "#efc39b",
      sheet: "#e8a0b8",
      label: tr("產台室", "Delivery room"),
      act: "dr",
    },
  ],
  labels: {
    bed: tr("待產床：宜蓁、婆婆", "Labor bed: Yi-Zhen, mother-in-law"),
    nurse: tr("美玲姐", "Mei-Ling"),
    vsroom: tr("值班室：林主任", "On-call room: Dr. Lin"),
    dr: tr("產台室", "Delivery room"),
    vs: tr("林主任", "Dr. Lin"),
  },
  help: {
    ctg: tr("胎心音與宮縮（會打開大螢幕）", "Fetal heart and contractions (opens full screen)"),
    bed: tr("問診、身體檢查（會打開特寫）", "History, physical exam (opens a close-up)"),
    book: tr("產檢紀錄：GBS、血型、過敏", "Prenatal record: GBS, blood type, allergies"),
    bed2: tr("空床", "Empty bed"),
    sink: tr("洗手台", "Sink"),
    phone: tr("打給林主任", "Call Dr. Lin"),
    lab: tr("抽血結果", "Lab results"),
    nurse: tr("抽血、給藥、口頭醫囑", "Labs, meds, verbal orders"),
    vsroom: tr("林主任在睡覺，可以去叫醒他", "Dr. Lin is asleep; you can go wake him"),
    dr: tr("小周在接生", "Chou is delivering a baby"),
    vs: tr("林主任", "Dr. Lin"),
  },
  helpRules: tr(
    [
      "這是教學關：先把說明頁的地圖記一下。",
      "破水的病人：問清楚什麼時候開始、顏色、量、有沒有味道。",
      "產檢手冊上有重要的資訊，病人自己不一定記得。",
      "主任在值班室睡覺：可以打電話，也可以走過去敲門。",
    ],
    [
      "This is the tutorial: take a moment to learn the map on this page.",
      "Ruptured membranes: pin down when it started, the color, the amount, and any odor.",
      "The prenatal booklet holds key information the patient may not remember.",
      "The attending is asleep in the on-call room: you can call, or walk over and knock.",
    ],
  ),
  player: [10, 6],
  ents: () => [
    mkEnt({ k: "popo", id: "popo", x: 5, y: 3, act: "bed", face: "left" }),
    mkEnt({ k: "meiling", id: "meiling", x: 11, y: 4, act: "nurse" }),
    mkEnt({ k: "r2", id: "r2", x: 17, y: 9, act: "dr", face: "left" }),
  ],
  init: (): Vars => ({
    revealed: new Set(),
    bookRead: false,
    ctgSeen: false,
    ctgLogged: { normal: false, fever: false },
    padSeen: false,
    sse: false,
    ve: 0,
    bt: [],
    orders: {},
    abx: null,
    abxDrug: null,
    warned: {},
    reaction: null,
    reactionAt: null,
    fever: null,
    autoCall: null,
    vsAwake: false,
    vsTalked: false,
    vsAccepted: false,
    vsArriveAt: null,
    vsHere: false,
    labsSeen: false,
    peds: false,
    endAt: null,
  }),
  dialogs,
  zooms: { bed: bedZoom },
  quick: {
    ctg: openCTG,
    bed2: () => note(tr("空床。", "Empty bed.")),
    sink: () => note(tr("你洗了手。", "You wash your hands.")),
    dr: () =>
      toast({
        ch: "sys",
        kind: "npc",
        who: "r2",
        text: tr("我這邊在生！有事找主任！", "I've got a delivery going! Anything else, go to the attending!"),
        t: S.t,
      }),
  },

  onStart: () => eventBanner(tr("凌晨 03:00", "03:00"), tr("產房待產室", "L&D labor room")),
  tick(t) {
    if (t === 1)
      ask(
        "bed",
        "meiling",
        tr(
          "你先去問，問完跟主任報告。我在護理站。",
          "Go take her history, then present to the attending. I'll be at the station.",
        ),
        {
          id: "go",
          text: tr("美玲姐：先去問宜蓁", "Mei-Ling: go see Yi-Zhen first"),
          dlg: "bed",
          clearOn: "open",
          icon: tr("！", "!"),
        },
      );
    if (t === 18 && !S.vsTalked)
      ask("nurse", "meiling", tr("主任那邊報告了沒？", "Have you presented to the attending yet?"), {
        id: "rep",
        text: tr("美玲姐問你報告了沒", "Mei-Ling asks if you’ve presented yet"),
        dlg: "nurse",
      });
    const cbc = S.orders.cbc;
    if (cbc && !cbc.done && t >= (cbc.ready ?? Infinity)) {
      cbc.done = true;
      const txt = S.fever
        ? tr("WBC 18,900，CRP 4.2 mg/dL", "WBC 18,900, CRP 4.2 mg/dL")
        : tr("WBC 13,800，CRP 1.6 mg/dL", "WBC 13,800, CRP 1.6 mg/dL");
      finding(LABS, txt);
      post("nurse", "result", "meiling", tr(`抽血結果：${txt}`, `Lab results: ${txt}`));
    }
    if (S.reactionAt === t) {
      S.reaction = t;
      eventBanner(tr("藥物過敏", "Drug allergy"), tr("全身起疹子、發癢", "Rash all over, itching"), true);
      post(
        "bed",
        "alert",
        "popo",
        tr(
          "醫生！她身上起了好多紅疹子，一直說很癢！",
          "Doctor! She's breaking out in red spots all over and says it itches like crazy!",
        ),
      );
      post(
        "nurse",
        "npc",
        "meiling",
        tr(
          "抗生素我先停了。手冊上寫她盤尼西林過敏。",
          "I've stopped the antibiotic. Her booklet says she's allergic to penicillin.",
        ),
      );
      S.mood.meiling = (S.mood.meiling ?? 0) + 1;
    }
    if (!S.fever && !S.abx && t >= 35) {
      S.fever = t;
      eventBanner(tr("產時發燒", "Intrapartum fever"), tr("38.6°C　胎心 172", "38.6°C · FHR 172"), true);
      post("bed", "alert", "popo", tr("醫生！她在發抖，摸起來好燙！", "Doctor! She's shaking, and she's burning up!"));
      post(
        "nurse",
        "alert",
        "meiling",
        tr(
          "醫師，耳溫 38.6，胎心掉不下來，baseline 170 以上了。",
          "Doctor, tympanic temp 38.6, and the fetal heart won't come down. Baseline's over 170.",
        ),
      );
      if (!S.vsAwake) S.autoCall = t + 4;
    }
    if (S.autoCall === t && !S.vsAccepted) {
      post("nurse", "npc", "meiling", tr("我直接叫主任了。", "I'm calling the attending myself."));
      wakeVS("nurse");
      S.vsAccepted = true;
      const alive = sessionGuard();
      setTimeout(() => {
        if (alive()) vsWalk();
      }, 600);
    }
    if (S.endAt === t) {
      if (S.fever) endB();
      else endA();
    }
  },
  timeup: () =>
    void playEnding(
      "Z",
      tr("交班", "Sign-out"),
      [
        {
          who: "meiling",
          text: tr("天亮了，白班來接了。你回去睡吧。", "It's morning, day shift is here. Go get some sleep."),
        },
      ],
      tr("時間到，交班。", "Time's up. Sign-out."),
    ),

  hud() {
    const b = S.bt.at(-1);
    return [
      [
        b ? tr(`${clock(b.t)} 體溫`, `Temp ${clock(b.t)}`) : tr("體溫", "Temp"),
        b ? `${b.v}°C` : tr("還沒量", "Not taken"),
      ],
      [
        tr("抗生素", "Abx"),
        S.abx !== null ? drugName() : S.reaction ? tr("過敏，已停", "Allergy, stopped") : tr("還沒給", "None yet"),
      ],
    ];
  },
  urgent: () => !!S.fever,
  drawBelow(c, now) {
    const px = 2 * T;
    const py = 3 * T;
    c.fillStyle = "#8a90a2";
    c.fillRect(px + 7, py + 6, 2, 10);
    c.fillStyle = "#3a3f52";
    c.fillRect(px + 1, py, 14, 9);
    c.fillStyle = "#f6eef0";
    c.fillRect(px + 2, py + 1, 12, 7);
    c.fillStyle = "#203a8a";
    for (let i = 0; i < 12; i++) {
      const y = py + 3 + Math.round(1.5 * Math.sin((i + now / 200) * 0.9));
      c.fillRect(px + 2 + i, y, 1, 1);
    }
    DRAW.bed?.(c, 3 * T, 3 * T, { pt: true, hair: "#2b1d18", skin: "#f1cfb8", sheet: "#9fc4a8" });
    if (!S.vsAwake) DRAW.bed?.(c, 17 * T, 2 * T, { pt: true, hair: "#b9b9c4", skin: "#ecc19a", sheet: "#7886cc" });
    else DRAW.bed?.(c, 17 * T, 2 * T, {});
    if (!S.vsAwake && Math.floor(now / 700) % 2)
      ptext(c, "Z", 17 * T + 11, 2 * T - 4 + (Math.floor(now / 700) % 3), "#d6dbf0");
  },

  graderCase: tr(
    `教學關。32 歲初產婦，38+3 週，凌晨說褲子濕。真相：昨天早上八點多就破水（到凌晨已超過 18 小時，她自己以為是昨晚，婆婆知道真正時間），羊水淡綠（胎便染色），GBS 沒做（產檢手冊寫未做，病人以為有做），盤尼西林過敏（手冊有寫）。正確處置：問清楚破水時間與性狀、看護墊與無菌窺器確認、避免反覆內診、量體溫、看 CTG、查產檢手冊、因破水超過 18 小時且 GBS 不明給預防性抗生素（盤尼西林過敏要改用 Cefazolin 或 Clindamycin）、胎便染色通知小兒科、向主治報告後引產。35 分鐘內沒有給有效抗生素，病人會發燒（疑似絨毛膜羊膜炎）。`,
    `Tutorial level. A 32-year-old primigravida at 38+3 weeks says in the small hours that her pants are wet. Truth: her membranes ruptured a little after 8 a.m. yesterday (more than 18 hours by now; she thinks it was last night, and her mother-in-law knows the real time), the fluid is pale green (meconium-stained), GBS was never done (the prenatal booklet says not done; the patient thinks it was), and she is allergic to penicillin (noted in the booklet). Correct management: pin down the time and character of the rupture, confirm with the pad and a sterile speculum exam, avoid repeated cervical exams, take her temperature, review the CTG, check the prenatal booklet, give prophylactic antibiotics because ROM is over 18 hours with GBS unknown (penicillin allergy: switch to cefazolin or clindamycin), notify pediatrics because of the meconium, and induce after presenting to the attending. If no effective antibiotic is given within 35 minutes, the patient develops a fever (suspected chorioamnionitis).`,
  ),
  rubric: tr(
    [
      "破水病史：時間（含從婆婆問出真正時間）、顏色、量、氣味",
      "身體檢查：看護墊、無菌窺器、內診次數是否克制、體溫、CTG",
      "查產檢手冊：GBS 狀態與過敏",
      "抗生素：時機與藥物選擇（盤尼西林過敏）",
      "胎便染色的處理（通知小兒科）",
      "向主治報告（時機、內容、計畫）",
      "與病人和婆婆的溝通",
    ],
    [
      "History of the rupture: timing (including getting the real time from the mother-in-law), color, amount, odor",
      "Physical exam: pad, sterile speculum, restraint with cervical exams, temperature, CTG",
      "Checking the prenatal booklet: GBS status and allergies",
      "Antibiotics: timing and drug choice (penicillin allergy)",
      "Handling meconium-stained fluid (notifying pediatrics)",
      "Presenting to the attending (timing, content, plan)",
      "Communication with the patient and her mother-in-law",
    ],
  ),
  facts: () => [
    tr(
      `問出真正破水時間：${S.revealed.has("rom_true") ? "是" : "否"}`,
      `Got the real rupture time: ${S.revealed.has("rom_true") ? "yes" : "no"}`,
    ),
    tr(`產檢手冊：${S.bookRead ? "看過" : "沒看"}`, `Prenatal booklet: ${S.bookRead ? "read" : "not read"}`),
    tr(`內診：${S.ve} 次`, `Cervical exams: ${S.ve}`),
    tr(
      `抗生素：${S.abx !== null ? `${drugName()}（${clock(S.abx)}）` : "沒有有效給藥"}`,
      `Antibiotics: ${S.abx !== null ? `${drugName()} (${clock(S.abx)})` : "no effective antibiotic given"}`,
    ),
    tr(`過敏反應：${S.reaction ? "有" : "沒有"}`, `Allergic reaction: ${S.reaction ? "yes" : "no"}`),
    tr(`小兒科：${S.peds ? "有通知" : "沒通知"}`, `Pediatrics: ${S.peds ? "notified" : "not notified"}`),
    tr(`發燒：${S.fever ? `是（${clock(S.fever)}）` : "否"}`, `Fever: ${S.fever ? `yes (${clock(S.fever)})` : "no"}`),
  ],
  known,
};
